import React, {useCallback, useMemo} from 'react'
import {Modal, Button, Spinner, Badge, Alert, Table} from 'react-bootstrap'
import {useQuery} from '@apollo/client'
import moment from 'moment'
import {GET_POLICY_CARRIER_LAST_SNAPSHOT} from '../../../gql/queries/carrierImportQueries'

interface Props {
  show: boolean
  onClose: () => void
  policyId: string
}

const IMPORTANT_FIELDS = [
  'Broker Name',
  'Broker NPN',
  'Policy Number',
  'Plan Name',
  'Insured First Name',
  'Insured Last Name',
  'Broker Effective Date',
  'Broker Term Date',
  'Policy Effective Date',
  'Policy Term Date',
  'Paid Through Date',
  'Member Responsibility',
  'Monthly Premium Amount',
  'County',
  'State',
  'On/Off Exchange',
  'Exchange Subscriber ID',
  'Member Phone Number',
  'Member Email',
  'Member Date Of Birth',
  'Autopay',
  'Eligible for Commission',
  'Number of Members',
  'Payable Agent',
  'AR Policy Type',
  'ICHRA Indicator',
  'Employer Group Name',
  'Employer Start Date',
  'Employer Subsidy Amount',
  'Employer Subsidy Type',
  'Targeted Outreach Group',
  'Suggested Action',
]

const BCBS_FIELDS = [
  'Account Number',
  'Status',
  'Plan Name',
  'Coverage Effective Date',
  'Paid To Date',
  'Termed',
  'Producer Name',
  'Nine Digit Producer Number',
  'Exchange ID',
  'Client App ID',
  'Member Count',
  'Group Number',
  'Product Type',
  'APTC',
  'Renewal Indicator',
  'Last Name',
  'First Name',
  'Date of Birth',
  'Client Address 1',
  'Client Address 2',
  'City',
  'State',
  'Zip Code',
  "Client's Primary Phone",
  'Email',
]

const MOLINA_FIELDS = [
  'Broker_NPN',
  'Broker_First_Name',
  'Broker_Last_Name',
  'Subscriber_ID',
  'HIX_ID',
  'Status',
  'Product',
  'Effective_date',
  'End_Date',
  'Paid_Through_Date',
  'Net_Due_Amount',
  'Member_Premium',
  'Total_Premium',
  'Member_First_Name',
  'Member_Last_Name',
  'dob',
  'Gender',
  'Relationship',
  'Member_Count',
  'Member_Bussiness_Phone',
  'Address1',
  'Address2',
  'City',
  'State',
  'Zip',
  'Application_Date',
  'Original_Effective_Date',
  'Broker_Start_Date',
  'Broker_End_Date',
]

const ANTHEM_FIELDS = [
  'Client Name',
  'Client ID',
  'Market',
  'Status',
  'State',
  'Exchange',
  'Effective Date',
  'Original Effective Date',
  'Cancellation Date',
  'Product',
  'Plan Name',
  'New Business',
  'Bill Status',
  'Bill Due Date',
  'Renewal Month',
  'ACA',
  'Family ID',
  'Group Size',
  'Writing Agent',
  'Writing TIN',
  'Paid Agent',
  'Paid TIN',
  'Parent Agent',
  'Parent TIN',
  'Reporting Agent',
]

const IMPERIAL_FIELDS = [
  'First Name',
  'Middle Name',
  'Last Name',
  'Gender',
  'MBI',
  'Subscriber ID',
  'Member ID',
  'DOB',
  'Status',
  'EFF Start Date',
  'EFF End Date',
  'Subscriber Status',
  'Last Payment Date',
  'Last Payment Amount',
  'Plan',
  'State',
  'Agent',
  'Agency',
  'Assistant Agent',
]

const UNITED_FIELDS = [
  // Book of Business
  'agentId',
  'agentEmail',
  'agentName',
  'agentIdStatus',
  'agentNpn',
  'memberFirstName',
  'memberLastName',
  'memberAddress1',
  'memberAddress2',
  'memberCity',
  'memberZip',
  'memberState',
  'dateOfBirth',
  'memberPhone',
  'product',
  'planStatus',
  'memberNumber',
  'Agent of Record',
  'memberEmail',
  'policyEffectiveDate',
  'applicationNumber',
  'individualID',
  'policyTermDate',
  'termReasonCode',
  'householdID',
  'IFP - Plan Type',
  'IFP - Renewal',
  'planName',
  'memberCounty',
  'contract',
  'segmentId',
  'Plan Status(MS)',
  // Late Payment
  'Name',
  'Subscriber ID',
  'Phone',
  'Grace Period Days Left',
  'Paid Through',
  'Monthly Premium',
  'Balance Due',
  'Grace Period End Date',
  'Members Plan Name',
  'AOR Writing ID',
]

const SENTARA_FIELDS = [
  'Group Number',
  'Group Name',
  'Total Due',
  'Oldest Invoice with no Payment',
  'Agent Name',
  'Group Status',
]

const JEFFERSON_FIELDS = [
  'Member Name',
  'DOB',
  'Exchange Assigned ID',
  'Issuer Assigned ID',
  'Metal Tier',
  'Member Plan ID',
  'Member Plan Name',
  'Household Size',
  'Relationship',
  'Plan Effective Date',
  'Monthly Premium',
  'AutoPay Status',
  'Payment Status',
  'Account Status',
  'Paid Through Date',
  'Primary Contact',
  'Mailing Address',
  'County',
  'State',
  'Zip',
]

const INDEPENDENCE_FIELDS = [
  'UMI ID',
  'Customer Name',
  'DOB',
  'Agent',
  'Effective Date',
  'Cancel Date',
  'Member Enrollment Status',
  'Member Payment Status',
  'Paid Through Date',
  'Member Billed Amount',
  'Account#',
  'Source',
  'Line Of Business',
  'Plan Description',
]

const AMERIHEALTH_FIELDS = [
  'Member Name',
  'DOB',
  'Exchange Assigned ID',
  'Issuer Assigned ID',
  'Metal Tier',
  'Member Plan ID',
  'Member Plan Name',
  'Household Size',
  'Relationship',
  'Plan Effective Date',
  'Monthly Premium',
  'AutoPay Status',
  'Payment Status',
  'Account Status',
  'Paid Through Date',
  'Primary Contact',
  'Mailing Address',
  'County',
  'State',
  'Zip',
]

const CIGNA_FIELDS = [
  'Subscriber ID (Detail Case #)',
  'Customer Number (Case ID)',
  'Primary Last Name',
  'Primary First Name',
  'Writing Agent NPN',
  'Writing Agent',
  'Product Type',
  'ON/OFF Exchange',
  'Subsidy',
  'Total Premium',
  'APTC',
  'Premium - Customer Responsibility',
  'Plan Name',
  'Policy Status',
  'Effective Date',
  'Date Application Received',
  'Renewal Month',
  'Paid Through Date',
  'Termination Date',
  'State',
  'Customer Email Address',
  'Customer Phone Number',
  'Application Id',
  'Coverage Status',
  'Agent Start Date',
  'Agent End Date',
]

const ALLIANT_FIELDS = [
  'Status',
  'Plan Type',
  'Policy ID',
  'Broker First Name',
  'Broker Last Name',
  'First Name',
  'Last Name',
  'DOB',
  'Email',
  'Home Phone',
  'Work Phone',
  'BENPKG',
  'EFFDAT',
  'EXPDAT',
]

const HIGHMARK_FIELDS = [
  'Member_ID',
  'First_Name',
  'Last_Name',
  'DOB',
  'Plan_Name',
  'Start_Date',
  'End_Date',
  'Group_ID',
  'Rep_NPN',
  'Rep_Name',
  'On_Exchange',
]

const EXCLUDED_FIELDS = new Set([
  'Policy Number',
  'Insured First Name',
  'Insured Last Name',
  'Policy Effective Date',
  'Policy Term Date',
  'Member Date Of Birth',
  'County',
  'State',
  'On/Off Exchange',
  'ICHRA Indicator',
  'Employer Group Name',
  'Employer Start Date',
  'Employer Subsidy Amount',
  'Employer Subsidy Type',
])

export const ModalCarrierStatusDetails: React.FC<Props> = ({show, onClose, policyId}) => {
  const {data, loading, error} = useQuery(GET_POLICY_CARRIER_LAST_SNAPSHOT, {
    variables: {policyId},
    skip: !policyId || !show,
    fetchPolicy: 'network-only',
  })

  const snapshot = data?.policyCarrierLastSnapshot
  const batch = snapshot?.batch
  const rawRow = useMemo(() => {
    const raw = snapshot?.rawRow
    if (!raw) return null
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw) as Record<string, unknown>
      } catch {
        return null
      }
    }
    if (typeof raw === 'object') return raw as Record<string, unknown>
    return null
  }, [snapshot?.rawRow])

  const reportDate = batch?.asOfDate ? moment(batch.asOfDate, 'YYYY-MM-DD', true) : null
  const buildPaidThroughValue = useCallback((value: unknown) => {
    if (!value) return {text: '', isDelinquent: false}
    const paidThroughStr = String(value)
    if (!reportDate || !reportDate.isValid()) return paidThroughStr
    const paidThrough = moment(paidThroughStr, ['MM/DD/YYYY', 'YYYY-MM-DD'], true)
    if (!paidThrough.isValid()) return paidThroughStr
    const days = reportDate.diff(paidThrough, 'days')
    if (days <= 0) return {text: paidThroughStr, isDelinquent: false}
    let group = ''
    if (days <= 60) group = '30 days (1-60 days)'
    else if (days <= 90) group = '60 days (61-90 days)'
    else group = '90 days (91+ days)'
    return {
      text: `${paidThroughStr} — ${days} days past due (${group})`,
      isDelinquent: true,
    }
  }, [reportDate])

  const importantRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of IMPORTANT_FIELDS) {
      if (EXCLUDED_FIELDS.has(key)) continue
      const value = rawRow[key]
      if (key === 'Paid Through Date') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }
    return rows
  }, [buildPaidThroughValue, rawRow])

  const allRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    const oscarExclude = new Set([
      'Member ID',
      'Member name',
      'Date of birth',
      'Policy status',
      'Coverage end date',
      'Coverage start date',
    ])
    for (const [key, value] of Object.entries(rawRow)) {
      if (oscarExclude.has(key)) continue
      if (key === 'Paid Through Date') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }
    return rows
  }, [buildPaidThroughValue, rawRow])

  const carrierName = (batch?.carrier?.name || '').toLowerCase()
  const isOscar = carrierName.includes('oscar')
  const isBcbs = carrierName.includes('blue cross') || carrierName.includes('bcbs')
  const isMolina = carrierName.includes('molina')
  const isAnthem = carrierName.includes('anthem') || carrierName.includes('wellpoint')
  const isImperial = carrierName.includes('imperial')
  const isSentara = carrierName.includes('sentara')
  const isJefferson = carrierName.includes('jefferson')
  const isIndependence = carrierName.includes('independence')
  const isAmerihealth =
    (carrierName.includes('amerihealth') || carrierName.includes('ameri health')) &&
    !carrierName.includes('caritas')
  const isCigna = carrierName === 'cigna'
  const isAlliant = carrierName.includes('alliant')
  const isHighmark = carrierName.includes('highmark')
  const isUnited = carrierName.includes('united')

  const bcbsRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of BCBS_FIELDS) {
      const value = rawRow[key]
      rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
    }
    return rows
  }, [rawRow])

  const molinaRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of MOLINA_FIELDS) {
      const value = rawRow[key]
      if (key === 'Paid_Through_Date') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }
    return rows
  }, [buildPaidThroughValue, rawRow])

  const anthemRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of ANTHEM_FIELDS) {
      const value = rawRow[key]
      if (key === 'Bill Due Date') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }
    return rows
  }, [buildPaidThroughValue, rawRow])

  const imperialRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of IMPERIAL_FIELDS) {
      const value = rawRow[key]
      rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
    }
    return rows
  }, [rawRow])

  const unitedRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    const knownUnitedFields = new Set(UNITED_FIELDS)
    for (const key of UNITED_FIELDS) {
      const value = rawRow[key]
      if (key === 'Paid Through' || key === 'Bill Due Date') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }

    // Append any extra keys coming from either United file format.
    const extraKeys = Object.keys(rawRow).filter((key) => !knownUnitedFields.has(key))
    extraKeys.sort((a, b) => a.localeCompare(b))
    for (const key of extraKeys) {
      const value = rawRow[key]
      if (key === 'Paid Through' || key === 'Bill Due Date') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }
    return rows
  }, [buildPaidThroughValue, rawRow])

  const sentaraRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of SENTARA_FIELDS) {
      const value = rawRow[key]
      const isDelinquent = key === 'Total Due' && value !== null && value !== undefined && Number(value) > 0
      rows.push({
        label: key,
        value: value === null || value === undefined ? '' : String(value),
        isDelinquent,
      })
    }
    return rows
  }, [rawRow])

  const jeffersonRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of JEFFERSON_FIELDS) {
      const value = rawRow[key]
      if (key === 'Paid Through Date') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }
    return rows
  }, [buildPaidThroughValue, rawRow])

  const independenceRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of INDEPENDENCE_FIELDS) {
      const value = rawRow[key]
      if (key === 'Paid Through Date') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }
    return rows
  }, [buildPaidThroughValue, rawRow])

  const amerihealthRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of AMERIHEALTH_FIELDS) {
      const value = rawRow[key]
      if (key === 'Paid Through Date') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }
    return rows
  }, [buildPaidThroughValue, rawRow])

  const cignaRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of CIGNA_FIELDS) {
      const value = rawRow[key]
      if (key === 'Paid Through Date') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }
    return rows
  }, [buildPaidThroughValue, rawRow])

  const alliantRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of ALLIANT_FIELDS) {
      const value = rawRow[key]
      if (key === 'EXPDAT') {
        const result = buildPaidThroughValue(value)
        if (typeof result === 'string') {
          rows.push({label: key, value: result})
        } else {
          rows.push({label: key, value: result.text, isDelinquent: result.isDelinquent})
        }
      } else {
        rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
      }
    }
    return rows
  }, [buildPaidThroughValue, rawRow])

  const highmarkRows = useMemo(() => {
    if (!rawRow) return []
    const rows: Array<{label: string; value: string; isDelinquent?: boolean}> = []
    for (const key of HIGHMARK_FIELDS) {
      const value = rawRow[key]
      rows.push({label: key, value: value === null || value === undefined ? '' : String(value)})
    }
    return rows
  }, [rawRow])

  const displayRows = isOscar
    ? allRows
    : isBcbs
      ? bcbsRows
      : isMolina
        ? molinaRows
        : isAnthem
          ? anthemRows
          : isImperial
            ? imperialRows
            : isSentara
              ? sentaraRows
            : isJefferson
              ? jeffersonRows
            : isIndependence
              ? independenceRows
            : isAmerihealth
              ? amerihealthRows
            : isCigna
              ? cignaRows
            : isAlliant
              ? alliantRows
            : isHighmark
              ? highmarkRows
            : isUnited
              ? unitedRows
        : importantRows

  const pairedRows = useMemo(() => {
    const pairs: Array<[typeof importantRows[0] | null, typeof importantRows[0] | null]> = []
    for (let i = 0; i < displayRows.length; i += 2) {
      pairs.push([displayRows[i] || null, displayRows[i + 1] || null])
    }
    return pairs
  }, [displayRows])

  return (
    <Modal show={show} onHide={onClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>Carrier Status Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className='d-flex justify-content-center p-4'>
            <Spinner animation='border' />
          </div>
        ) : error ? (
          <Alert variant='danger'>Error loading carrier details: {error.message}</Alert>
        ) : !snapshot ? (
          <Alert variant='info'>No carrier snapshot found for this policy.</Alert>
        ) : (
          <>
            <div className='mb-3'>
              <h6 className='mb-2'>Snapshot</h6>
              <div className='d-flex flex-wrap gap-3'>
                <div>
                  <strong>Status:</strong>{' '}
                  <Badge bg='secondary'>
                    {snapshot.statusGeneric === 'IN_FORCE' && snapshot.carrierStatusRaw
                      ? `${snapshot.carrierStatusRaw} (IN_FORCE)`
                      : (snapshot.carrierStatusRaw || snapshot.statusGeneric || 'N/A')}
                  </Badge>
                </div>
                {snapshot.asOfDate && (
                  <div>
                    <strong>As-of:</strong> {snapshot.asOfDate}
                  </div>
                )}
                {snapshot.balance !== null && snapshot.balance !== undefined && (
                  <div>
                    <strong>Balance:</strong> {snapshot.balance}
                  </div>
                )}
                {snapshot.premium !== null && snapshot.premium !== undefined && (
                  <div>
                    <strong>Premium:</strong> {snapshot.premium}
                  </div>
                )}
                {snapshot.autopay !== null && snapshot.autopay !== undefined && (
                  <div>
                    <strong>Autopay:</strong> {snapshot.autopay ? 'Yes' : 'No'}
                  </div>
                )}
                {snapshot.eligibleForCommission !== null &&
                  snapshot.eligibleForCommission !== undefined && (
                    <div>
                      <strong>Eligible:</strong>{' '}
                      {snapshot.eligibleForCommission ? 'Yes' : 'No'}
                    </div>
                  )}
              </div>
            </div>

            <div className='mb-3'>
              <h6 className='mb-2'>Member</h6>
              <div className='d-flex flex-wrap gap-3'>
                <div>
                  <strong>Name:</strong>{' '}
                  {`${snapshot.memberFirstName || ''} ${snapshot.memberLastName || ''}`.trim() ||
                    'N/A'}
                </div>
                <div>
                  <strong>Member ID:</strong> {snapshot.memberCarrierId || 'N/A'}
                </div>
                {snapshot.memberDob && (
                  <div>
                    <strong>DOB:</strong> {snapshot.memberDob}
                  </div>
                )}
                {snapshot.policyNumber && (
                  <div>
                    <strong>Policy #:</strong> {snapshot.policyNumber}
                  </div>
                )}
                {snapshot.effectiveDate && (
                  <div>
                    <strong>Effective:</strong> {snapshot.effectiveDate}
                  </div>
                )}
                {snapshot.termDate && (
                  <div>
                    <strong>Term:</strong> {snapshot.termDate}
                  </div>
                )}
              </div>
            </div>

            {rawRow && (
              <div className='mb-3'>
                <h6 className='mb-2'>{isOscar ? 'All File Fields' : 'Key File Fields'}</h6>
                <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                  <Table striped bordered hover size='sm'>
                    <tbody>
                      {pairedRows.map(([left, right], idx) => (
                        <tr key={idx}>
                          <td>{left?.label ? <strong>{left.label}</strong> : ''}</td>
                          <td className={left?.isDelinquent ? 'text-danger' : undefined}>
                            {left?.value || ''}
                          </td>
                          <td>{right?.label ? <strong>{right.label}</strong> : ''}</td>
                          <td className={right?.isDelinquent ? 'text-danger' : undefined}>
                            {right?.value || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}

            {batch && (
              <div className='mb-2'>
                <h6 className='mb-2'>Last Import</h6>
                <div className='d-flex flex-wrap gap-3'>
                  <div>
                    <strong>Carrier:</strong> {batch.carrier?.name || 'N/A'}
                  </div>
                  <div>
                    <strong>File:</strong> {batch.originalFilename || 'N/A'}
                  </div>
                  {batch.uploadedAt && (
                    <div>
                      <strong>Uploaded:</strong>{' '}
                      {moment(batch.uploadedAt).format('MM/DD/YYYY hh:mm A')}
                    </div>
                  )}
                  {batch.uploadedBy && (
                    <div>
                      <strong>By:</strong> {batch.uploadedBy.firstName}{' '}
                      {batch.uploadedBy.lastName}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
